import React from 'react';
import { useTranslation } from 'react-i18next';
import { RegionFlags } from './RegionFlag';
import './AppleSignInButton.css';

interface AppleSignInButtonProps {
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  /**
   * Secondary line naming the app and region this button signs into. Set when more than one Apple
   * button is shown, because the user's Apple account resolves to a different identity per app -
   * picking the wrong button does not sign them into their existing account.
   *
   * Comes from client configuration rather than i18n: it carries brand names set per environment.
   */
  label?: string;
  /** Comma-separated region codes for the flags, e.g. 'se,no,dk,fr'. Rendered alongside the label. */
  regions?: string;
}

/**
 * Apple Sign-In Button Component
 * Provides Apple-branded sign-in button with proper styling and accessibility
 * Follows Apple's Human Interface Guidelines for Sign in with Apple
 */
export const AppleSignInButton: React.FC<AppleSignInButtonProps> = ({
  onClick,
  disabled = false,
  loading = false,
  className = '',
  label = '',
  regions = ''
}) => {
  const { t } = useTranslation();

  const handleClick = () => {
    if (!disabled && !loading) {
      onClick();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled && !loading) {
      e.preventDefault();
      onClick();
    }
  };

  const accessibleLabel = label
    ? `${t('apple.signInLabel')}, ${label}`
    : t('apple.signInLabel');

  return (
    <button
      type="button"
      className={`apple-signin-button ${label ? 'apple-signin-button--stacked' : ''} ${className} ${disabled ? 'apple-signin-button--disabled' : ''} ${loading ? 'apple-signin-button--loading' : ''}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={disabled || loading}
      aria-label={loading ? t('apple.signingIn') : accessibleLabel}
      aria-describedby="apple-signin-description"
    >
      <div className="apple-signin-button__content">
        {loading ? (
          <div className="apple-signin-button__spinner" aria-hidden="true" />
        ) : (
          <svg
            className="apple-signin-button__icon"
            viewBox="0 0 24 24"
            width="18"
            height="18"
            aria-hidden="true"
          >
            <path
              fill="currentColor"
              d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"
            />
          </svg>
        )}

        <span className="apple-signin-button__labels">
          <span className="apple-signin-button__text">
            {loading ? t('apple.signingInShort') : t('apple.signInLabel')}
          </span>
          {label && !loading && (
            <span className="apple-signin-button__sublabel">{label}</span>
          )}
        </span>

        {label && !loading && (
          <RegionFlags
            regions={regions}
            className="apple-signin-button__flags"
            flagClassName="apple-signin-button__flag"
          />
        )}
      </div>
    </button>
  );
};
